import React, { useState, useEffect } from 'react';
import { ChevronRight, X, Building2, Users, Globe, MapPin, Lock, Clock, Image as ImageIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GalleryPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [selectedCategory, setSelectedCategory] = useState(tabParam || 'office-space');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update selected category when URL parameter changes
  useEffect(() => {
    if (tabParam) {
      setSelectedCategory(tabParam);
    }
  }, [tabParam]);

  const categories = [
    {
      id: 'office-space',
      name: 'Office Space',
      icon: Building2,
      count: 12
    },
    {
      id: 'coworking',
      name: 'Coworking',
      icon: Users,
      count: 8
    },
    {
      id: 'virtual-office',
      name: 'Virtual Office',
      icon: Globe,
      count: 6
    },
    {
      id: 'meeting-rooms',
      name: 'Meeting Rooms',
      icon: Users,
      count: 10
    },
    {
      id: 'business-addresses',
      name: 'Business Addresses',
      icon: MapPin,
      count: 4
    },
    {
      id: 'private-offices',
      name: 'Private Offices',
      icon: Lock,
      count: 15
    },
    {
      id: 'day-offices',
      name: 'Day Offices',
      icon: Clock,
      count: 7
    },
    {
      id: 'virtual-tour',
      name: 'Virtual Tour',
      icon: Globe,
      count: 5
    }
  ];

  const galleryImages = {
    'office-space': [
      {
        id: 1,
        src: './images-optimized/galery-images/IMG_6840.webp',
        alt: 'Single Seater Office Space',
        title: 'Single Seater Office',
        description: 'Modern single seater office with all amenities'
      },
      {
        id: 2,
        src: './images-optimized/galery-images/IMG_6813.webp',
        alt: '2 Seater Cabin',
        title: '2 Seater Cabin',
        description: 'Collaborative workspace for small teams'
      },
      {
        id: 3,
        src: './images-optimized/galery-images/IMG_E6811.webp',
        alt: '6 Seater Office',
        title: '6 Seater Office',
        description: 'Spacious office for growing teams'
      },
      {
        id: 4,
        src: './images-optimized/galery-images/IMG_E6845.webp',
        alt: 'Executive Office',
        title: 'Executive Office',
        description: 'Premium executive workspace'
      },
      {
        id: 5,
        src: './images-optimized/galery-images/IMG_E6838.webp',
        alt: 'Office Interior',
        title: 'Office Interior',
        description: 'Professional office interior design'
      },
      {
        id: 6,
        src: './images-optimized/galery-images/IMG_E6855.webp',
        alt: 'Workspace Setup',
        title: 'Workspace Setup',
        description: 'Complete workspace setup with modern furniture'
      }
    ],
    'coworking': [
      {
        id: 7,
        src: './images-optimized/galery-images/IMG_E6855.webp',
        alt: 'Hot Desk Area',
        title: 'Hot Desk Area',
        description: 'Flexible hot desk workspace'
      },
      {
        id: 8,
        src: './images-optimized/galery-images/NTLE0082.webp',
        alt: 'Dedicated Desk',
        title: 'Dedicated Desk',
        description: 'Personal dedicated workspace'
      },
      {
        id: 9,
        src: './images-optimized/galery-images/Meeting Room .webp',
        alt: 'Team Collaboration Area',
        title: 'Team Collaboration Area',
        description: 'Open collaboration space for teams'
      },
      {
        id: 10,
        src: './images-optimized/galery-images/IMG_E6838.webp',
        alt: 'Coworking Lounge',
        title: 'Coworking Lounge',
        description: 'Comfortable lounge area for networking'
      }
    ],
    'virtual-office': [
      {
        id: 11,
        src: './images-optimized/RECPTN AND LOUNGE/IMG_E6806.webp',
        alt: 'Virtual Office Setup',
        title: 'Virtual Office Setup',
        description: 'Professional virtual office environment'
      },
      {
        id: 12,
        src: './images-optimized/RECPTN AND LOUNGE/IMG_6839.webp',
        alt: 'Business Address Location',
        title: 'Business Address Location',
        description: 'Premium business address location'
      },
      {
        id: 13,
        src: './images-optimized/RECPTN AND LOUNGE/IMG_0217.JPG.jpeg',
        alt: 'Reception Area',
        title: 'Reception Area',
        description: 'Professional reception for virtual office clients'
      }
    ],
    'meeting-rooms': [
      {
        id: 14,
        src: './images-optimized/galery-images/IMG_6855.webp',
        alt: 'Conference Room',
        title: 'Conference Room',
        description: 'Professional conference room with AV equipment'
      },
      {
        id: 15,
        src: './images-optimized/galery-images/IMG_6850.webp',
        alt: 'Small Meeting Room',
        title: 'Small Meeting Room',
        description: 'Intimate meeting space for small groups'
      },
      {
        id: 16,
        src: './images-optimized/CONFERENCE/IMG_E6854.webp',
        alt: 'Executive Boardroom',
        title: 'Executive Boardroom',
        description: 'Premium boardroom for executive meetings'
      },
      {
        id: 17,
        src: './images-optimized/galery-images/Meeting Room .webp',
        alt: 'Video Conference Room',
        title: 'Video Conference Room',
        description: 'Modern video conferencing facility'
      }
    ],
    'business-addresses': [
      {
        id: 18,
        src: './images-optimized/ENTRANCE/IMG_6760.webp',
        alt: 'Business Building Exterior',
        title: 'Business Building Exterior',
        description: 'Professional business building exterior'
      },
      {
        id: 19,
        src: './images-optimized/galery-images/IMG_E6838.webp',
        alt: 'Business Lobby',
        title: 'Business Lobby',
        description: 'Impressive business lobby area'
      }
    ],
    'private-offices': [
      {
        id: 20,
        src: './images-optimized/galery-images/IMG_6813.webp',
        alt: 'Private Executive Office',
        title: 'Private Executive Office',
        description: 'Luxurious private executive office'
      },
      {
        id: 21,
        src: './images-optimized/6 SEAT/IMG_6809.webp',
        alt: 'Custom Private Office',
        title: 'Custom Private Office',
        description: 'Fully customized private workspace'
      },
      {
        id: 22,
        src: './images-optimized/galery-images/NTLE0082.webp',
        alt: 'Private Office Interior',
        title: 'Private Office Interior',
        description: 'Professional private office interior'
      }
    ],
    'day-offices': [
      {
        id: 23,
        src: './images-optimized/galery-images/IMG_E6855.webp',
        alt: 'Daily Office Space',
        title: 'Daily Office Space',
        description: 'Flexible daily office rental'
      },
      {
        id: 24,
        src: './images-optimized/1 SEAT/IMG_6844.webp',
        alt: 'Focus Room',
        title: 'Focus Room',
        description: 'Quiet focus room for concentrated work'
      },
      {
        id: 25,
        src: './images-optimized/1 SEAT/IMG_6845.webp',
        alt: 'Hourly Office',
        title: 'Hourly Office',
        description: 'Professional hourly office rental'
      }
    ],
    'virtual-tour': [
      {
        id: 26,
        type: 'video',
        videoId: 'n2DMj8oxO10',
        title: 'Complete Office Space Tour',
        description: 'Take a comprehensive virtual tour of our modern office spaces and amenities',
        thumbnail: './images-optimized/galery-images/IMG_6789.webp'
      },
      // {
      //   id: 27,
      //   type: 'video',
      //   videoId: 'jNQXAC9IVRw',
      //   title: 'Coworking Space Experience',
      //   description: 'Explore our vibrant coworking spaces and collaborative work environments',
      //   thumbnail: './images-optimized/galery-images/IMG_E6855.webp'
      // },
      // {
      //   id: 28,
      //   type: 'video',
      //   videoId: 'ScMzIvxBSi4',
      //   title: 'Meeting Room Facilities',
      //   description: 'Discover our state-of-the-art meeting rooms and conference facilities',
      //   thumbnail: './images-optimized/galery-images/IMG_6813.webp'
      // },
      // {
      //   id: 29,
      //   type: 'video',
      //   videoId: '9bZkp7q19f0',
      //   title: 'Private Office Solutions',
      //   description: 'See how our private offices can be customized for your business needs',
      //   thumbnail: './images-optimized/galery-images/NTLE0082.webp'
      // },
      // {
      //   id: 30,
      //   type: 'video',
      //   videoId: 'kJQP7kiw5Fk',
      //   title: 'Complete Facility Walkthrough',
      //   description: 'A complete walkthrough of all our facilities, amenities, and services',
      //   thumbnail: './images-optimized/galery-images/GALO0952.webp'
      // }
    ]
  };

  // Auto-open first video when virtual-tour tab is selected
  useEffect(() => {
    if (selectedCategory === 'virtual-tour' && tabParam === 'virtual-tour') {
      const virtualTourVideos = galleryImages['virtual-tour'];
      if (virtualTourVideos && virtualTourVideos.length > 0) {
        setSelectedVideo(virtualTourVideos[0]);
      }
    }
  }, [selectedCategory, tabParam]);

  const currentImages = galleryImages[selectedCategory] || [];
  const currentCategory = categories.find(cat => cat.id === selectedCategory);

  const openImageModal = (image) => {
    setSelectedImage(image);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Workspace Gallery
          </h1>
          <p className="text-lg text-gray-600">
            Explore our premium workspace solutions through our comprehensive image gallery
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Category Toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="flex items-center space-x-3">
                {currentCategory && (
                  <>
                    <currentCategory.icon size={20} className="text-[#e30613]" />
                    <span className="font-medium text-gray-900">{currentCategory.name}</span>
                    <span className="text-sm text-gray-500">({currentCategory.count} images)</span>
                  </>
                )}
              </div>
              <ChevronRight 
                size={20} 
                className={`text-gray-400 transform transition-transform ${mobileMenuOpen ? 'rotate-90' : ''}`} 
              />
            </button>
          </div>

          {/* Categories Sidebar */}
          <div className={`lg:w-80 ${mobileMenuOpen ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <ImageIcon size={24} className="text-[#e30613] mr-2" />
                Categories
              </h2>
              
              <nav className="space-y-2">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  const isActive = selectedCategory === category.id;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-[#e30613] text-white'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#e30613]'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent 
                          size={20} 
                          className={isActive ? 'text-white' : 'text-gray-500'} 
                        />
                        <span className="font-medium">{category.name}</span>
                      </div>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        isActive 
                          ? 'bg-white/20 text-white' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {category.count}
                      </span>
                    </button>
                  );
                })}
              </nav>

              {/* Category Info */}
              {currentCategory && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {currentCategory.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedCategory === 'virtual-tour' 
                      ? `Experience ${currentCategory.count} immersive virtual tours of our workspace solutions and facilities.`
                      : `Browse through ${currentCategory.count} high-quality images of our ${currentCategory.name.toLowerCase()} solutions.`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Image Gallery */}
          <div className="flex-1">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              {/* Gallery Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {currentCategory?.name} {selectedCategory === 'virtual-tour' ? '' : 'Gallery'}
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">
                    {selectedCategory === 'virtual-tour' 
                      ? `${currentImages.length} virtual tours available` 
                      : `${currentImages.length} images available`
                    }
                  </p>
                </div>
              </div>

              {/* Gallery Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentImages.map((item) => (
                  <div
                    key={item.id}
                    className="group cursor-pointer"
                    onClick={() => item.type === 'video' ? openVideoModal(item) : openImageModal(item)}
                  >
                    <div className="relative overflow-hidden rounded-lg bg-gray-100 aspect-[4/3]">
                      {item.type === 'video' ? (
                        <>
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            loading="lazy"
                          />
                          {/* Play Button Overlay */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-black/50 rounded-full p-4 group-hover:bg-black/70 transition-all duration-300">
                              <svg 
                                className="w-8 h-8 text-white" 
                                fill="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                          {/* Video Badge */}
                          <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                            VIRTUAL TOUR
                          </div>
                        </>
                      ) : (
                        <>
                          <img
                            src={item.src}
                            alt={item.alt}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300" />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2">
                              <ImageIcon size={24} className="text-[#e30613]" />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="mt-3">
                      <h3 className="font-semibold text-gray-900 text-sm group-hover:text-[#e30613] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty State */}
              {currentImages.length === 0 && (
                <div className="text-center py-12">
                  {selectedCategory === 'virtual-tour' ? (
                    <Globe size={48} className="text-gray-300 mx-auto mb-4" />
                  ) : (
                    <ImageIcon size={48} className="text-gray-300 mx-auto mb-4" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {selectedCategory === 'virtual-tour' ? 'No virtual tours available' : 'No images available'}
                  </h3>
                  <p className="text-gray-600">
                    {selectedCategory === 'virtual-tour' 
                      ? 'Virtual tours for this category will be added soon.' 
                      : 'Images for this category will be added soon.'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="relative">
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-gray-600">
                  {selectedImage.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-5xl w-full max-h-full bg-black rounded-lg overflow-hidden">
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors"
            >
              <X size={24} />
            </button>
            
            <div className="relative">
              {/* YouTube Embed */}
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedVideo.title}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              
              {/* Video Info */}
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {selectedVideo.title}
                </h3>
                <p className="text-gray-600">
                  {selectedVideo.description}
                </p>
                <div className="mt-4 flex items-center text-sm text-gray-500">
                  <Globe size={16} className="mr-2" />
                  <span>Virtual Tour Experience</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
